import * as React from 'react';

import { useSetUserTimezone } from './mutations/useSetUserTimezone';

interface ITimezoneSyncUser {
  id: string;
  inferredTimezone?: string | null;
}

/**
 * Pure helper extracted for testability: given the last inferred timezone on
 * the user record, the timezone the browser is currently reporting, and the
 * value we most recently synced this session, decide what (if anything) to
 * push to the server. Returning a string fires `setUserTimezone` with that
 * value; `null` is a no-op. The hook below wires this into a React effect.
 *
 * Compares against `inferredTimezone`, never `timezone`: the latter resolves
 * to whatever the user stated, so comparing against it would push the browser
 * value on every load for anyone who told us where they live.
 */
export function decideTimezoneSync({
  inferredTimezone,
  browserTimezone,
  lastSynced,
}: {
  inferredTimezone: string | null | undefined;
  browserTimezone: string | undefined;
  lastSynced: string | null;
}): string | null {
  if (!browserTimezone) {
    return null;
  }
  if (inferredTimezone === browserTimezone) {
    return null;
  }
  if (lastSynced === browserTimezone) {
    return null;
  }
  return browserTimezone;
}

/**
 * Syncs the browser's reported IANA timezone to the user record on each
 * app load. Fires the mutation when the user is authenticated AND the
 * browser timezone differs from the last inferred value on the user. The
 * server falls back to this when the user has stated no zone of their own —
 * keeping it auto-updated keeps "today" anchored to their actual locale even
 * when they travel.
 *
 * Idempotent: the effect dep array includes `user.inferredTimezone`, so the
 * moment the server-reported value matches the browser value the mutation is
 * skipped on subsequent renders. A `synced` ref guards against firing more
 * than once per (user, browser-tz) pair while the mutation is in flight or
 * before the new value lands in the Apollo cache.
 */
export const useTimezoneSync = (user: ITimezoneSyncUser | null): void => {
  const [setUserTimezone] = useSetUserTimezone();
  const syncedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!user) {
      syncedRef.current = null;
      return;
    }

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const next = decideTimezoneSync({
      inferredTimezone: user.inferredTimezone,
      browserTimezone,
      lastSynced: syncedRef.current,
    });
    if (next === null) {
      return;
    }

    syncedRef.current = next;
    void setUserTimezone({ variables: { timezone: next } });
  }, [user, setUserTimezone]);
};
